// The grounding brief's question model. One entry per bullet the backend seed renders, in the
// order it renders them (services/py/shared/strategy/business_context_markdown.py, GROUNDING_LABELS).
//
// `mdLabel` is the functional contract: it is the key the markdown is parsed on and composed
// with, so it must match the Python renderer byte for byte. Drift is caught by the golden seed
// in __fixtures__ — pytest asserts the renderer still produces it, groundingMarkdown.test.ts
// asserts this list still reads it, so neither side can move alone.
//
// The question/helper/placeholder copy is ours; the placeholders deliberately mirror the seed's
// own coaching hints, which is why a fact we could not infer still reads as guidance rather than
// as an empty box.

export type GroundingSection = 'today' | 'goal' | 'plan';

export type GroundingFieldId =
	| 'whatWeDo'
	| 'website'
	| 'whereWeOperate'
	| 'howWeSell'
	| 'whoWeServe'
	| 'goal'
	| 'newOfferings'
	| 'whatsWorking'
	| 'bottleneck'
	| 'constraints'
	| 'horizon';

export interface GroundingField {
	id: GroundingFieldId;
	section: GroundingSection;
	mdLabel: string;
	question: string;
	/** Short noun phrase on the Spectrum control itself; the question is the heading above it. */
	label: string;
	helper: string;
	placeholder: string;
	kind: 'short' | 'long';
}

export const SECTION_HEADINGS: Record<GroundingSection, string> = {
	today: 'Where we are today',
	goal: 'Where we want to go',
	plan: 'How we get there',
};

export const GROUNDING_FIELDS: readonly GroundingField[] = [
	{
		id: 'whatWeDo',
		section: 'today',
		mdLabel: 'What we do',
		label: 'What you do',
		question: 'What does your business do?',
		helper:
			"And what you do better than the alternatives — that's the part we build on.",
		placeholder:
			'What your business does, and does better than the alternatives',
		kind: 'long',
	},
	{
		id: 'website',
		section: 'today',
		mdLabel: 'Website',
		label: 'Website',
		question: "What's your website?",
		helper:
			'We read it to keep the strategy grounded in what you actually sell.',
		placeholder: 'yourbusiness.com',
		kind: 'short',
	},
	{
		id: 'whereWeOperate',
		section: 'today',
		mdLabel: 'Where we operate',
		label: 'Where you operate',
		question: 'Where do you operate?',
		helper:
			'Your real service area — this decides whether local moves are worth it.',
		placeholder: 'A neighborhood, a city, or online-only',
		kind: 'short',
	},
	{
		id: 'howWeSell',
		section: 'today',
		mdLabel: 'How we sell',
		label: 'How you sell',
		question: 'How do you sell?',
		helper: 'We know your connected tools. Add the human detail we cannot see.',
		placeholder:
			'Solo or a team, home-based or a storefront, online or in person',
		kind: 'long',
	},
	{
		id: 'whoWeServe',
		section: 'today',
		mdLabel: 'Who we serve today',
		label: 'Who you serve',
		question: 'Who do you serve today?',
		helper:
			'Be specific — the biggest reachable market is often not the obvious one.',
		placeholder: 'The customers you actually serve',
		kind: 'long',
	},
	{
		id: 'goal',
		section: 'goal',
		mdLabel: 'Our goal',
		label: 'Goal',
		question: 'What do you most want to grow?',
		helper: 'And to what number, if you have one.',
		placeholder:
			'What you most want to grow — and to what number, if you have one',
		kind: 'long',
	},
	{
		id: 'newOfferings',
		section: 'goal',
		mdLabel: 'New offerings or repositioning',
		label: 'New offerings',
		question: "Anything new you're launching?",
		helper: "Things that aren't on your website yet — we'd never guess these.",
		placeholder: "Anything new you're launching or moving toward",
		kind: 'long',
	},
	{
		id: 'whatsWorking',
		section: 'plan',
		mdLabel: "What's working now",
		label: "What's working",
		question: "What's already earning you customers?",
		helper: 'So we build on those moves instead of duplicating them.',
		placeholder: 'The moves already earning you customers',
		kind: 'long',
	},
	{
		// The owner's own diagnosis. Its choices are BOTTLENECK_MATRIX rows. The backend CHOOSES
		// which rows fit this business, and serves them verbatim. The stored answer can then be
		// matched back to a row, and the generator held to it (services/py/shared/strategy/catalog.py).
		// It sits beside "What's already earning you customers?" because the two are one
		// diagnosis: what is working, and what is not.
		id: 'bottleneck',
		section: 'plan',
		mdLabel: "What's holding us back",
		label: 'The blocker',
		question: "What's holding you back?",
		helper: 'The one thing most in the way. We build the plan to break it.',
		placeholder: 'The one thing most limiting growth right now',
		kind: 'short',
	},
	{
		// Replaced the old `capacity` question, which asked only for hours. A plan that ignores
		// what the business cannot deliver has its payoff land nowhere, so both halves are asked
		// together — and the hours ask has to survive verbatim, because the feasibility self-test
		// reads it to size the plan.
		id: 'constraints',
		section: 'plan',
		mdLabel: 'Time and limits to plan around',
		label: 'What to plan around',
		question: 'What should we plan around?',
		helper:
			"Hours a week and who helps, plus anything you can't deliver, won't do, or already tried.",
		placeholder: 'Hours a week, who helps, and what to keep off the plan',
		kind: 'long',
	},
	{
		id: 'horizon',
		section: 'plan',
		mdLabel: 'How long it runs',
		label: 'How long',
		question: 'How long should this run before we judge it?',
		helper: 'One to four months works best. It gives the work time to land.',
		placeholder: 'Three months',
		kind: 'short',
	},
] as const;

export type GroundingAnswers = Partial<Record<GroundingFieldId, string>>;

// The walk asks the number right after the goal question, so the owner says what they want to
// grow in their own words FIRST and only then puts a figure on it. It used to appear for the
// first time on the summary, as a row nobody had been asked about, which read as a fault
// (ADO-1963).
//
// It is a STEP, not a twelfth entry in GROUNDING_FIELDS: it is stored under its own keys rather
// than as an answer, and it never becomes a bullet in the brief.
export const NORTH_STAR_STEP =
	GROUNDING_FIELDS.findIndex((f) => f.id === 'goal') + 1;

/** Every question in the walk, the north-star step included. */
export const TOTAL_STEPS = GROUNDING_FIELDS.length + 1;

/**
 * The number, shaped like every other question so it is asked like every other question: the same
 * composed turn, the same answer cards, the same escape hatch.
 *
 * It is deliberately NOT a member of GROUNDING_FIELDS. Only that list becomes bullets in the
 * brief, and the number is stored under its own keys so the prose and the machine value stay
 * separable.
 */
export const NORTH_STAR_FIELD: GroundingField = {
	id: 'northStar' as GroundingFieldId,
	section: 'goal',
	mdLabel: '',
	label: 'Your number',
	// Standalone on purpose. The server grounds every question against the brief and the answers
	// so far, so this reads back with what they just said they want to grow — but a degraded model
	// falls back to this text verbatim, and "what number are you aiming for?" on its own is a
	// question about nothing.
	question: 'What number should we judge this strategy on?',
	helper:
		'Tie it to what they just said they want to grow. Offer concrete targets, each with a figure and the thing it counts.',
	placeholder: '500 subscribers, or $50,000 in revenue',
	kind: 'short',
};

/** True at the step that asks for the number. */
export const isNorthStarStep = (step: number): boolean =>
	step === NORTH_STAR_STEP;

/** What a step asks about. */
export const fieldForStep = (step: number): GroundingField | null => {
	if (step === NORTH_STAR_STEP) return NORTH_STAR_FIELD;
	return GROUNDING_FIELDS[step < NORTH_STAR_STEP ? step : step - 1] ?? null;
};

/**
 * The one tally for the whole flow.
 *
 * Three places count this walk — the "N remaining" row, the fill meter, and the summary — and each
 * used to derive its own denominator. They drifted: the summary listed twelve rows and said
 * "11 of 11 filled in" (ADO-1963). They all read this now, so a new question moves every one of
 * them at once.
 *
 * Two figures, because they answer different questions and always did:
 *   `filled`  — questions holding a value, whoever put it there. A guess of ours IS filled, which
 *               is fair for a progress meter and for a summary of what we know.
 *   `checked` — questions the OWNER answered. "Facts to go" must use this: every field arrives
 *               pre-filled, so counting `filled` there claimed nothing was left while the
 *               interview was still asking.
 */
export interface GroundingTally {
	/** Every question in the walk, the number included. */
	total: number;
	filled: number;
	checked: number;
	/** Still to check, never negative. */
	remaining: number;
}

export const groundingTally = (
	answers: GroundingAnswers,
	hasNumber: boolean,
	checked: number,
): GroundingTally => ({
	total: TOTAL_STEPS,
	filled:
		GROUNDING_FIELDS.filter((f) => (answers[f.id] ?? '').trim()).length +
		(hasNumber ? 1 : 0),
	checked,
	remaining: Math.max(0, TOTAL_STEPS - checked),
});

/** The fields already asked before this step, in the order they were asked. */
export const fieldsBeforeStep = (step: number): GroundingField[] =>
	GROUNDING_FIELDS.slice(0, step <= NORTH_STAR_STEP ? step : step - 1);

/** The fields not yet asked at this step, in the order they will be asked. */
export const fieldsFromStep = (step: number): GroundingField[] =>
	GROUNDING_FIELDS.slice(step <= NORTH_STAR_STEP ? step : step - 1);

// The north star is confirmed on the summary, not asked as one of the nine questions, so it is
// stored as a correction rather than an answer: `northStar` is what we read back to the owner and
// what rides the brief, `northStarValue` is the machine form the backend reads.
//
// Two keys rather than one because they answer different questions. The prose has to survive being
// shown to a person; the wire value has to survive being parsed. Deriving either from the other
// means an LLM or a regex re-reading our own output, which is how the target got invented in the
// first place.
export const NORTH_STAR_PROSE_KEY = 'northStar';
export const NORTH_STAR_VALUE_KEY = 'northStarValue';
// What the owner typed when their goal is off-vocabulary, kept verbatim and on its own.
//
// It is not recoverable from the other two: the wire value holds only `other:<n>`, and pulling the
// words back out of the prose sentence means a regex re-reading our own output. The nearest thing
// to hand — their answer to the interview's goal question — is a DIFFERENT statement, so reusing
// it puts words in the row they never typed there.
export const NORTH_STAR_WORDS_KEY = 'northStarWords';

export type GroundingCorrectionId =
	| GroundingFieldId
	| typeof NORTH_STAR_PROSE_KEY
	| typeof NORTH_STAR_VALUE_KEY
	| typeof NORTH_STAR_WORDS_KEY;

/** The eight outcomes a strategy can be measured on, plus the way out for everything else. */
export const OUTCOME_KEYS = [
	'revenue',
	'sales',
	'bookings',
	'customers',
	'leads',
	'signups',
	'subscribers',
	'followers',
] as const;

export type OutcomeKey = (typeof OUTCOME_KEYS)[number];

/** Off-vocabulary marker. Stored so the owner's own words keep their number, but it never maps
 * to an outcome key, so nothing holds the strategy to a measure they did not choose. */
export const OUTCOME_OTHER = 'other';

// The one place these keys become words. The backend owns the vocabulary (its validator and
// prompts key off it); rendering is ours, so the labels live here rather than being mirrored.
export const OUTCOME_LABEL: Record<OutcomeKey, string> = {
	revenue: 'Revenue',
	sales: 'Sales',
	bookings: 'Bookings',
	customers: 'Customers',
	leads: 'Leads',
	signups: 'Sign-ups',
	subscribers: 'Subscribers',
	followers: 'Followers',
};

/** What we call the thing being counted, mid-sentence. */
export const OUTCOME_NOUN: Record<OutcomeKey, string> = {
	revenue: 'in revenue',
	sales: 'sales',
	bookings: 'bookings',
	customers: 'customers',
	leads: 'leads',
	signups: 'sign-ups',
	subscribers: 'subscribers',
	followers: 'followers',
};

// Every coaching hint the Python seed can emit (GROUNDING_HINTS in
// business_context_markdown.py). The parser strips a trailing emphasis run ONLY when it matches
// one of these literally — pattern-matching italics instead would silently eat an owner's own
// emphasis, and today's editor ships an Italic button.
export const GROUNDING_HINTS: readonly string[] = [
	'add a line on what your business does and does better than the alternatives',
	'add your real service area — a neighborhood, a city, or online-only',
	'Add the human detail — solo or a team, home-based or a storefront, online or in person.',
	'add detail here — solo or a team, home-based or a storefront, online or in person',
	'(our best guess from your site — correct it; the biggest reachable market is often not the obvious one)',
	'add the customers you actually serve — be specific; the biggest reachable market is often not the obvious one',
	'what do you most want to grow, and to what number if you have one?',
	"Add anything NEW you're launching or moving toward that isn't on the website yet.",
	"anything new you're launching or moving toward that isn't on the website yet",
	'(the moves already earning customers — add results or edit so we build on them instead of duplicating them)',
	'the moves already earning customers, so we build on them instead of duplicating them',
	'the one thing most limiting growth right now — pick the closest and we build the plan to break it',
	"hours a week and who helps, plus anything you can't deliver, won't do, or have already tried",
	'how long before we judge it — one to four months works best',
];

// Hints the seed no longer emits, because ADO-2049 retired the questions that carried them.
//
// Kept ONLY so a brief saved before that change can still have OUR prose recognised. Their
// labels are no longer in `byLabel`, so such a bullet now takes the unknown-label branch and is
// preserved verbatim as the owner's earlier notes. For a question the owner never answered, that
// preserves nothing but our own coaching question — and then shows it back to them as their words
// and hands it to the strategist as owner prose, which is the ADO-1983 failure exactly.
//
// Deliberately NOT part of GROUNDING_HINTS: that list is what a LIVE field's value is stripped
// against, and `test_every_declared_hint_is_reachable` requires every member to still be
// renderable. These are neither.
// A retired question's answer belongs in the question that replaced it. Without this the bullet
// falls to the unknown-label branch and becomes unplaceable text: the owner's real answer stops
// appearing against any question, and they are asked for it again.
//
// Only a DIRECT successor belongs here. `Steering` has none — it was a catch-all whose halves went
// to two different questions — so its text stays in `extra`, preserved in the brief but not
// re-asked as though it answered something it did not.
export const RETIRED_LABEL_SUCCESSORS: Readonly<
	Record<string, GroundingFieldId>
> = {
	'Time the owner has': 'constraints',
};

// Keyed BY LABEL, not a flat list. A hint literal is evidence about the one bullet the seed wrote
// it under; under any other label the same words are the owner's, and deleting them would breach
// the preserve-verbatim contract on a save that cannot be undone.
export const RETIRED_GROUNDING_HINTS: Readonly<
	Record<string, readonly string[]>
> = {
	'Time the owner has': [
		'hours a week you can give this, and whether anyone helps',
	],
	Steering: [
		'Add anything else you\'d tell a strategist — "more digital", "more local/outdoor", things you\'ve tried that flopped, constraints we should respect.',
		'anything you\'d tell a strategist — "more digital", "more local/outdoor", things you\'ve tried that flopped, constraints we should respect',
	],
};

// The seed's own chrome. Parsing must DROP these rather than bucket them into `extra`, or the
// first compose re-emits them and appends them again — duplicating the doc's head as a tail and
// showing our own template back to the owner as their earlier notes.
export const SEED_INTRO =
	"These are the facts we're building your strategy on. Correct anything that's wrong or out of date, and add what we couldn't know from your website — the strategy is only as good as what's below.";
